import createDebug from 'debug';
import { Tags, FORMAT_HTTP_HEADERS, Span, Tracer } from 'opentracing';
import * as Logs from './logs';

const dbg = createDebug('tracing:fetch');

export type ShouldTraceCallback = (request: Request) => boolean;
export type BeforeRequestCallback = (span: Span, request: Request) => void;
export type AfterResponseCallback = (span: Span, request: Response) => void;
export type ExtractOperationCallback = (request: Request) => string;

type TracedFetchOptions = {
  // A window.fetch-compatible implementatio
  fetch: WindowOrWorkerGlobalScope['fetch'];
  // An opentracing-compatible Tracer instance
  tracer: Tracer;
  // The fetch's Request is passed. Returning false prevents tracing the request.
  shouldTrace?: ShouldTraceCallback;
  // A hook to access the request object and span before the request is made.
  beforeRequest?: BeforeRequestCallback;
  // A hook to access the response object and span after the response is received.
  afterResponse?: AfterResponseCallback;
  // A hook to replace the span's default operation name (fetch).
  extractOperation?: ExtractOperationCallback;
};

/**
 * By default, create a `fetch` that will trace minimal logs/tags and handle
 * errors. For more useful tags, the user must use the
 * beforeRequest/afterResponse and the provided helpers.
 */
export function createTracedFetch(
  options: TracedFetchOptions,
): WindowOrWorkerGlobalScope['fetch'] {
  const {
    fetch: originalFetch,
    tracer,
    shouldTrace = () => true,
    beforeRequest = () => {},
    afterResponse = () => {},
    extractOperation = () => 'fetch',
  } = options;
  return async function mrktFetch(input, init) {
    const request = new Request(input, init);

    dbg('fetch %s', request.url);
    if (!shouldTrace(request)) {
      dbg('skipping %s', request.url);
      return originalFetch(request);
    }

    const operation = extractOperation(request);
    const span = tracer.startSpan(operation);

    const carrier: { [key: string]: string } = {};
    tracer.inject(span.context(), FORMAT_HTTP_HEADERS, carrier);
    Object.keys(carrier).forEach(key => {
      if (!request.headers.get(key)) request.headers.set(key, carrier[key]);
    });

    beforeRequest(span, request);

    let response;
    try {
      span.log({ event: 'request send' });
      response = await originalFetch(request);
    } catch (err) {
      span.setTag(Tags.ERROR, true);
      span.log({
        [Logs.EVENT]: 'error',
        [Logs.ERROR_OBJECT]: err,
        [Logs.ERROR_KIND]: err.name,
        [Logs.MESSAGE]: err.message,
        [Logs.STACK]: err.stack,
      });
      span.finish();
      throw err;
    }

    span.log({ event: 'response received' });

    afterResponse(span, response);

    span.finish();
    return response;
  };
}
